import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

/**
 * Enhanced Image component with fallback handling and lazy loading
 * 
 * @param {string} src - Primary image source URL
 * @param {string} fallbackSrc - Fallback image source URL if primary fails
 * @param {string} alt - Alternative text for the image
 * @param {boolean} lazy - Enable lazy loading (default: true)
 * @param {object} props - Additional props to pass to the img element
 */
const ImageWithFallback = ({ src, fallbackSrc, alt, lazy = true, ...props }) => {
  const [imgSrc, setImgSrc] = useState(src || fallbackSrc);
  
  // Update imgSrc when src prop changes and reset to use the new src
  useEffect(() => {
    setImgSrc(src || fallbackSrc);
  }, [src, fallbackSrc]);
  
  const handleError = () => {
    if (imgSrc !== fallbackSrc) {
      setImgSrc(fallbackSrc);
    }
  };

  return (
    <img 
      src={imgSrc} 
      alt={alt}
      onError={handleError}
      loading={lazy ? 'lazy' : 'eager'}
      {...props}
    />
  );
};

ImageWithFallback.propTypes = {
  src: PropTypes.string,
  fallbackSrc: PropTypes.string.isRequired,
  alt: PropTypes.string.isRequired,
  lazy: PropTypes.bool
};

export default ImageWithFallback;